import React, { useState, useEffect, useCallback } from 'react';
import { complianceProAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import IconPicker from '../components/IconPicker';
import './CompliancePro.css';

/* Compliance Pro — jurisdiction-wise upsell editor.
 *
 * Authors the three tables behind the v3 company-import "coverage" step:
 * the annual Compliance Pro plan (offering), the "what Compliance Pro files"
 * list (coverage), and the optional add-ons. v3 reads this live via
 * GET /api/compliance-pro/[code]. Uses the shared admin design system. */

const BILLINGS = ['yr', 'mo', 'filing', 'state'];

export default function CompliancePro() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selCode, setSelCode] = useState(null);
  const [toast, setToast] = useState(null);
  const [newCode, setNewCode] = useState('');

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await complianceProAPI.list();
      setList(r.data?.data?.jurisdictions || []);
    } catch { setList([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const addJurisdiction = () => {
    const code = String(newCode || '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
    if (!code) return;
    setNewCode('');
    setSelCode(code); // the editor create-or-edits by code; saving persists it
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">COMPLIANCE PRO</h1>
            <p className="page-subtitle">The jurisdiction-wise upsell shown at the company-import coverage step. Edits go live in the founder app immediately.</p>
          </div>
          <div className="cp-add">
            <input
              className="form-input"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addJurisdiction()}
              placeholder="Code e.g. GB"
            />
            <button className="btn-primary" onClick={addJurisdiction}>+ Add jurisdiction</button>
          </div>
        </div>

        <div className="dashboard-content">
          {toast && <div className="cp-toast">{toast}</div>}

          <div className="cp-grid">
            <div className="cp-list">
              {loading ? (
                <div className="cp-list-empty">Loading…</div>
              ) : list.length === 0 ? (
                <div className="cp-list-empty">No jurisdictions configured yet.</div>
              ) : list.map((j) => (
                <button key={j.code} className={`cp-list-item${selCode === j.code ? ' active' : ''}`} onClick={() => setSelCode(j.code)}>
                  <span style={{ minWidth: 0 }}>
                    <span className="cp-list-code">{j.code}</span>
                    <span className="cp-list-meta">
                      {j.has_offering ? `${j.price_label || '—'} · ` : 'no plan · '}{j.coverage_count} filings · {j.addon_count} add-ons
                    </span>
                  </span>
                  {(!j.has_offering || !j.is_active) && (
                    <span className="cp-list-badge">{j.has_offering ? 'off' : 'no plan'}</span>
                  )}
                </button>
              ))}
            </div>

            <div>
              {selCode ? (
                <JurisdictionEditor
                  key={selCode}
                  code={selCode}
                  onSaved={async () => { await loadList(); setToast('Saved — live in the founder app.'); }}
                  onDeleted={async () => { setSelCode(null); await loadList(); setToast('Removed.'); }}
                />
              ) : (
                <div className="cp-empty">Select a jurisdiction to edit, or add one by its country code.</div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const emptyOffering = () => ({ pro_name: 'Compliance Pro', amount: 0, billing: 'yr', price_label: '', blurb: '', is_active: 1 });

function JurisdictionEditor({ code, onSaved, onDeleted }) {
  const [hasPro, setHasPro] = useState(true);
  const [offering, setOffering] = useState(emptyOffering());
  const [coverage, setCoverage] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await complianceProAPI.get(code);
      const d = r.data?.data || {};
      if (d.offering) { setHasPro(true); setOffering({ ...emptyOffering(), ...d.offering }); }
      else { setHasPro(false); setOffering(emptyOffering()); }
      setCoverage((d.coverage || []).map((c) => ({ icon: c.icon || 'check', label: c.label || '', items_text: c.items_text || '' })));
      setAddons((d.addons || []).map((a) => ({
        sku_code: a.sku_code || '', name: a.name || '', amount: a.amount || 0, billing: a.billing || 'mo',
        price_label: a.price_label || '', blurb: a.blurb || '', ask_question: a.ask_question || '',
        trigger_when: a.trigger_when || '', is_active: a.is_active === 0 ? 0 : 1,
      })));
    } catch { setErr('Failed to load'); } finally { setLoading(false); }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const setOff = (k) => (v) => setOffering((p) => ({ ...p, [k]: v }));

  const addCoverage = () => setCoverage((p) => [...p, { icon: 'check', label: '', items_text: '' }]);
  const setCov = (i, k, v) => setCoverage((p) => p.map((r, ri) => (ri === i ? { ...r, [k]: v } : r)));
  const delCov = (i) => setCoverage((p) => p.filter((_, ri) => ri !== i));
  const moveCov = (i, d) => setCoverage((p) => {
    const j = i + d; if (j < 0 || j >= p.length) return p;
    const n = p.slice(); [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const addAddon = () => setAddons((p) => [...p, { sku_code: '', name: '', amount: 0, billing: 'mo', price_label: '', blurb: '', ask_question: '', trigger_when: '', is_active: 1 }]);
  const setAdd = (i, k, v) => setAddons((p) => p.map((r, ri) => (ri === i ? { ...r, [k]: v } : r)));
  const delAddon = (i) => setAddons((p) => p.filter((_, ri) => ri !== i));

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await complianceProAPI.save(code, {
        offering: hasPro ? {
          pro_name: offering.pro_name, amount: Number(offering.amount) || 0, billing: offering.billing,
          price_label: offering.price_label, blurb: offering.blurb, is_active: offering.is_active === 0 ? 0 : 1,
        } : null,
        coverage: coverage.filter((c) => String(c.label || '').trim()),
        addons: addons.filter((a) => String(a.sku_code || '').trim() && String(a.name || '').trim())
          .map((a) => ({ ...a, amount: Number(a.amount) || 0 })),
      });
      await onSaved();
      await load();
    } catch (e) {
      setErr(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove all Compliance Pro config for ${code}?`)) return;
    setSaving(true);
    try { await complianceProAPI.delete(code); await onDeleted(); }
    catch { setErr('Delete failed'); setSaving(false); }
  };

  if (loading) return <div className="cp-empty">Loading…</div>;

  return (
    <div>
      <div className="cp-editor-head">
        <h2 className="cp-editor-title">{code}</h2>
        <div className="cp-editor-actions">
          <button className="cp-btn-danger" onClick={remove} disabled={saving}>Delete</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
      {err && <div className="cp-error">{err}</div>}

      {/* Plan */}
      <div className="cp-card">
        <div className="cp-card-head">
          <h3 className="cp-card-title">COMPLIANCE PRO PLAN</h3>
          <label className="cp-check">
            <input type="checkbox" checked={hasPro} onChange={(e) => setHasPro(e.target.checked)} />
            Offer Compliance Pro here
          </label>
        </div>
        {hasPro ? (
          <>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">PLAN NAME</label>
                <input className="form-input" value={offering.pro_name} onChange={(e) => setOff('pro_name')(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">PRICE LABEL (shown to founder)</label>
                <input className="form-input" value={offering.price_label} onChange={(e) => setOff('price_label')(e.target.value)} placeholder="$2,499 / yr" />
              </div>
            </div>
            <div className="form-grid" style={{ marginTop: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">AMOUNT (USD, whole dollars)</label>
                <input type="number" className="form-input" value={offering.amount} onChange={(e) => setOff('amount')(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">BILLING</label>
                <select className="form-input" value={offering.billing} onChange={(e) => setOff('billing')(e.target.value)}>
                  {BILLINGS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
              <label className="form-label">BLURB</label>
              <textarea className="form-input cp-textarea" value={offering.blurb} onChange={(e) => setOff('blurb')(e.target.value)} placeholder="One line on what the plan covers." />
            </div>
            <div className="form-group" style={{ marginTop: 'var(--space-md)', maxWidth: 260 }}>
              <label className="form-label">VISIBILITY</label>
              <select className="form-input" value={offering.is_active ? 1 : 0} onChange={(e) => setOff('is_active')(Number(e.target.value))}>
                <option value={1}>Active — show to founders</option>
                <option value={0}>Hidden</option>
              </select>
            </div>
          </>
        ) : (
          <p className="cp-hint" style={{ margin: 0 }}>No plan offered here — founders will only see the “handle filings myself” option.</p>
        )}
      </div>

      {/* Coverage — the "what Compliance Pro files" list */}
      <div className="cp-card">
        <div className="cp-card-head">
          <h3 className="cp-card-title">WHAT COMPLIANCE PRO FILES</h3>
          <button className="cp-btn-ghost" onClick={addCoverage}>+ Add filing</button>
        </div>
        <p className="cp-hint">Each row is one line in the founder-facing list: an icon, a short heading, and the detail beneath it.</p>
        {coverage.length === 0 && <p className="cp-hint" style={{ margin: 0 }}>No filings listed yet.</p>}
        {coverage.map((c, i) => (
          <div key={i} className="cp-item">
            <div className="cp-item-head">
              <span className="cp-item-label">FILING {i + 1}</span>
              <div className="cp-item-tools">
                <button className="cp-icon-btn" onClick={() => moveCov(i, -1)} title="Move up">↑</button>
                <button className="cp-icon-btn" onClick={() => moveCov(i, 1)} title="Move down">↓</button>
                <button className="cp-icon-btn danger" onClick={() => delCov(i)} title="Remove">✕</button>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">ICON</label>
                <IconPicker value={c.icon} onChange={(name) => setCov(i, 'icon', name)} />
              </div>
              <div className="form-group">
                <label className="form-label">HEADING</label>
                <input className="form-input" value={c.label} onChange={(e) => setCov(i, 'label', e.target.value)} placeholder="e.g. Federal tax" />
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
              <label className="form-label">DETAIL</label>
              <textarea className="form-input cp-textarea" value={c.items_text} onChange={(e) => setCov(i, 'items_text', e.target.value)} placeholder="What's filed, in plain words — e.g. Form 1120, with Form 5472 where foreign ownership requires it" />
            </div>
          </div>
        ))}
      </div>

      {/* Add-ons */}
      <div className="cp-card">
        <div className="cp-card-head">
          <h3 className="cp-card-title">ADD-ONS</h3>
          <button className="cp-btn-ghost" onClick={addAddon}>+ Add add-on</button>
        </div>
        <p className="cp-hint">Optional extras the founder can toggle on (bookkeeping, payroll, etc.).</p>
        {addons.length === 0 && <p className="cp-hint" style={{ margin: 0 }}>No add-ons yet.</p>}
        {addons.map((a, i) => (
          <div key={i} className="cp-item">
            <div className="cp-item-head">
              <span className="cp-item-label">ADD-ON {i + 1}</span>
              <div className="cp-item-tools">
                <button className="cp-icon-btn danger" onClick={() => delAddon(i)} title="Remove">✕</button>
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">SKU CODE</label>
                <input className="form-input" value={a.sku_code} onChange={(e) => setAdd(i, 'sku_code', e.target.value)} placeholder="SKU-US-BOOKS" />
              </div>
              <div className="form-group">
                <label className="form-label">NAME</label>
                <input className="form-input" value={a.name} onChange={(e) => setAdd(i, 'name', e.target.value)} placeholder="Bookkeeping" />
              </div>
            </div>
            <div className="form-grid" style={{ marginTop: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">AMOUNT (USD)</label>
                <input type="number" className="form-input" value={a.amount} onChange={(e) => setAdd(i, 'amount', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">BILLING</label>
                <select className="form-input" value={a.billing} onChange={(e) => setAdd(i, 'billing', e.target.value)}>
                  {BILLINGS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div className="form-grid" style={{ marginTop: 'var(--space-md)' }}>
              <div className="form-group">
                <label className="form-label">PRICE LABEL</label>
                <input className="form-input" value={a.price_label} onChange={(e) => setAdd(i, 'price_label', e.target.value)} placeholder="$150 / mo" />
              </div>
              <div className="form-group">
                <label className="form-label">VISIBILITY</label>
                <select className="form-input" value={a.is_active ? 1 : 0} onChange={(e) => setAdd(i, 'is_active', Number(e.target.value))}>
                  <option value={1}>Active</option>
                  <option value={0}>Hidden</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
              <label className="form-label">BLURB</label>
              <textarea className="form-input cp-textarea" value={a.blurb} onChange={(e) => setAdd(i, 'blurb', e.target.value)} placeholder="What this add-on covers." />
            </div>
            <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
              <label className="form-label">ASK (the question the founder answers)</label>
              <input className="form-input" value={a.ask_question} onChange={(e) => setAdd(i, 'ask_question', e.target.value)} placeholder="Do you want the books kept every month?" />
            </div>
            <div className="form-group" style={{ marginTop: 'var(--space-md)' }}>
              <label className="form-label">TRIGGER NOTE (when to switch on)</label>
              <input className="form-input" value={a.trigger_when} onChange={(e) => setAdd(i, 'trigger_when', e.target.value)} placeholder="Switch on once anyone is on US payroll." />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
